import type { DirectoryProgress, FileProgress, FileUploadState } from '.';

export const FILE_UPLOAD_INITIAL_STATE: FileUploadState = {
  files: [],
  directories: [],
  isDragging: false,
};

export type FileUploadAction =
  // Drag state
  | { type: 'drag/set'; isDragging: boolean }
  | { type: 'drag/reset' }
  // Files (top-level)
  | { type: 'files/addMany'; files: FileProgress[] }
  | { type: 'files/removeById'; id: string }
  | { type: 'files/clear' }
  | {
      type: 'files/updateById';
      id: string;
      patch: Partial<FileProgress>;
    }
  // Directories (tree)
  | { type: 'directories/addMany'; directories: DirectoryProgress[] }
  | { type: 'directories/removeById'; id: string }
  | { type: 'directories/clear' }
  | {
      type: 'directories/updateById';
      id: string;
      patch: Partial<DirectoryProgress>;
    }
  | {
      type: 'directories/updateFileById';
      directoryId: string;
      fileId: string;
      patch: Partial<FileProgress>;
    }
  | { type: 'directories/incrementUploadedFiles'; directoryId: string }
  | {
      type: 'directories/incrementCreatedSubdirectories';
      directoryId: string;
    }
  | { type: 'reset' };

export function fileUploadReducer(state: FileUploadState, action: FileUploadAction): FileUploadState {
  switch (action.type) {
    case 'drag/set': {
      if (state.isDragging === action.isDragging) return state;
      return { ...state, isDragging: action.isDragging };
    }

    case 'drag/reset': {
      if (!state.isDragging) return state;
      return { ...state, isDragging: false };
    }

    case 'files/addMany': {
      if (action.files.length === 0) return state;
      return { ...state, files: [...state.files, ...action.files] };
    }

    case 'files/removeById': {
      if (!state.files.some((f) => f.id === action.id)) return state;
      return { ...state, files: state.files.filter((f) => f.id !== action.id) };
    }

    case 'files/clear': {
      if (state.files.length === 0) return state;
      return { ...state, files: [] };
    }

    case 'files/updateById': {
      const next = state.files.map((f) => (f.id === action.id ? applyFilePatch(f, action.patch) : f));
      if (shallowArrayEqual(state.files, next)) return state;

      return { ...state, files: next };
    }

    case 'directories/addMany': {
      if (action.directories.length === 0) return state;
      return {
        ...state,
        directories: [...state.directories, ...action.directories],
      };
    }

    case 'directories/removeById': {
      if (!treeHasDirectoryId(state.directories, action.id)) return state;
      return {
        ...state,
        directories: removeDirectoryFromTree(state.directories, action.id),
      };
    }

    case 'directories/clear': {
      if (state.directories.length === 0) return state;
      return { ...state, directories: [] };
    }

    case 'directories/updateById': {
      if (!treeHasDirectoryId(state.directories, action.id)) return state;
      return {
        ...state,
        directories: updateDirectoryInTree(state.directories, action.id, action.patch),
      };
    }

    case 'directories/updateFileById': {
      if (!treeHasDirectoryId(state.directories, action.directoryId)) {
        return state;
      }
      return {
        ...state,
        directories: updateFileInDirectoryTree(state.directories, action.directoryId, action.fileId, action.patch),
      };
    }

    case 'directories/incrementUploadedFiles': {
      if (!treeHasDirectoryId(state.directories, action.directoryId)) {
        return state;
      }
      return {
        ...state,
        directories: incrementUploadedFilesInTree(state.directories, action.directoryId),
      };
    }

    case 'directories/incrementCreatedSubdirectories': {
      if (!treeHasDirectoryId(state.directories, action.directoryId)) {
        return state;
      }
      return {
        ...state,
        directories: incrementCreatedSubdirsInTree(state.directories, action.directoryId),
      };
    }

    case 'reset': {
      if (state.files.length === 0 && state.directories.length === 0 && state.isDragging === false) {
        return state;
      }
      return FILE_UPLOAD_INITIAL_STATE;
    }

    default: {
      // Exhaustive check
      const _exhaustive: never = action;
      return state;
    }
  }
}

function applyFilePatch(file: FileProgress, patch: Partial<FileProgress>): FileProgress {
  for (const key in patch) {
    if (file[key] !== patch[key]) {
      return { ...file, ...patch };
    }
  }
  return file;
}

function shallowArrayEqual<T>(a: T[], b: T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function treeHasDirectoryId(directories: DirectoryProgress[], targetId: string): boolean {
  for (const d of directories) {
    if (d.id === targetId) return true;
    if (d.subdirectories.length > 0) {
      if (treeHasDirectoryId(d.subdirectories, targetId)) return true;
    }
  }
  return false;
}

export function updateDirectoryInTree(directories: DirectoryProgress[], targetId: string, updates: Partial<DirectoryProgress>): DirectoryProgress[] {
  let changed = false;

  const next = directories.map((dir) => {
    if (dir.id === targetId) {
      const updated = applyDirectoryPatch(dir, updates);
      if (updated !== dir) changed = true;
      return updated;
    }

    if (dir.subdirectories.length > 0) {
      const updatedSubs = updateDirectoryInTree(dir.subdirectories, targetId, updates);
      if (updatedSubs !== dir.subdirectories) {
        changed = true;
        return { ...dir, subdirectories: updatedSubs };
      }
    }

    return dir;
  });

  return changed ? next : directories;
}

function applyDirectoryPatch(dir: DirectoryProgress, patch: Partial<DirectoryProgress>): DirectoryProgress {
  for (const key in patch) {
    if (dir[key] !== patch[key]) {
      return { ...dir, ...patch };
    }
  }
  return dir;
}

export function updateFileInDirectoryTree(directories: DirectoryProgress[], dirId: string, fileId: string, fileUpdates: Partial<FileProgress>): DirectoryProgress[] {
  let changed = false;

  const next = directories.map((dir) => {
    if (dir.id === dirId) {
      const nextFiles = dir.files.map((f) => (f.id === fileId ? applyFilePatch(f, fileUpdates) : f));

      if (!shallowArrayEqual(dir.files, nextFiles)) {
        changed = true;
        return { ...dir, files: nextFiles };
      }
      return dir;
    }

    if (dir.subdirectories.length > 0) {
      const nextSubs = updateFileInDirectoryTree(dir.subdirectories, dirId, fileId, fileUpdates);
      if (nextSubs !== dir.subdirectories) {
        changed = true;
        return { ...dir, subdirectories: nextSubs };
      }
    }

    return dir;
  });

  return changed ? next : directories;
}

export function incrementUploadedFilesInTree(directories: DirectoryProgress[], dirId: string): DirectoryProgress[] {
  let changed = false;

  const next = directories.map((dir) => {
    if (dir.id === dirId) {
      changed = true;
      return { ...dir, uploadedFiles: dir.uploadedFiles + 1 };
    }

    if (dir.subdirectories.length > 0) {
      const nextSubs = incrementUploadedFilesInTree(dir.subdirectories, dirId);
      if (nextSubs !== dir.subdirectories) {
        changed = true;
        return { ...dir, subdirectories: nextSubs };
      }
    }

    return dir;
  });

  return changed ? next : directories;
}

export function incrementCreatedSubdirsInTree(directories: DirectoryProgress[], dirId: string): DirectoryProgress[] {
  let changed = false;

  const next = directories.map((dir) => {
    if (dir.id === dirId) {
      changed = true;
      return {
        ...dir,
        createdSubdirectories: dir.createdSubdirectories + 1,
      };
    }

    if (dir.subdirectories.length > 0) {
      const nextSubs = incrementCreatedSubdirsInTree(dir.subdirectories, dirId);
      if (nextSubs !== dir.subdirectories) {
        changed = true;
        return { ...dir, subdirectories: nextSubs };
      }
    }

    return dir;
  });

  return changed ? next : directories;
}

export function removeDirectoryFromTree(directories: DirectoryProgress[], targetId: string): DirectoryProgress[] {
  let changed = false;

  const filtered = directories
    .filter((d) => {
      const keep = d.id !== targetId;
      if (!keep) changed = true;
      return keep;
    })
    .map((d) => {
      if (d.subdirectories.length === 0) return d;
      const nextSubs = removeDirectoryFromTree(d.subdirectories, targetId);
      if (nextSubs !== d.subdirectories) {
        changed = true;
        return { ...d, subdirectories: nextSubs };
      }
      return d;
    });

  return changed ? filtered : directories;
}
